import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SITE } from '@/constants/testIds';
import { scrollToId } from '@/lib/lenis';

const LINKS = [
  { slug: 'home', label: 'Home', target: 'top' },
  { slug: 'what', label: 'What We Do', target: 'what' },
  { slug: 'sectors', label: 'Sectors', target: 'sectors' },
  { slug: 'approach', label: 'Approach', target: 'approach' },
  { slug: 'team', label: 'Team', target: 'team' },
  { slug: 'work', label: 'Selected Work', target: 'work' },
  { slug: 'contact', label: 'Contact', target: 'contact' },
];

const LOGO_MARK =
  'https://customer-assets.emergentagent.com/job_80513a35-94e4-4bc1-b888-2940fba49aa8/artifacts/nu2t0p2r_WhatsApp%20Image%202026-07-03%20at%203.19.43%20PM.jpeg';

export const Nav = ({ onBook }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleNav = (target) => {
    setOpen(false);
    setTimeout(() => scrollToId(target), 40);
  };

  return (
    <motion.header
      data-testid={SITE.nav}
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? 'nav-shell' : ''
      }`}
    >
      <div className="mx-auto max-w-[1360px] px-5 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Wordmark */}
          <button
            onClick={() => handleNav('top')}
            className="flex items-center gap-3 group"
            aria-label="NoBS Advisors home"
          >
            <span className="relative w-9 h-9 rounded-full overflow-hidden border border-[color:var(--rule-dark-2)] shrink-0">
              <img
                src={LOGO_MARK}
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
              />
            </span>
            <span className="flex items-baseline gap-2">
              <span className="font-serif text-[19px] tracking-[-0.01em] text-[color:var(--stone)]">
                NoBS
              </span>
              <span className="small-caps text-[10px] text-[color:var(--stone-2)]">
                Advisors
              </span>
            </span>
          </button>

          {/* Links */}
          <nav
            data-testid={SITE.navMenu}
            className="hidden lg:flex items-center gap-1"
          >
            {LINKS.map((l) => (
              <button
                key={l.slug}
                data-testid={SITE.navLink(l.slug)}
                onClick={() => handleNav(l.target)}
                className="px-3 py-2 small-caps text-[11px] text-[color:var(--stone-2)] hover:text-[color:var(--gold-2)] transition-colors"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              data-testid={SITE.navBook}
              onClick={onBook}
              className="hidden md:inline-flex btn-primary !py-2.5 !px-4"
            >
              Start a Conversation
              <span aria-hidden>→</span>
            </button>
            <button
              className="lg:hidden inline-flex items-center justify-center w-10 h-10 border border-[color:var(--rule-dark-2)] rounded-sm"
              onClick={() => setOpen((v) => !v)}
              aria-label="Toggle menu"
            >
              <span className="relative block w-4 h-[10px]">
                <span
                  className={`absolute left-0 right-0 h-px bg-[color:var(--stone)] transition-transform ${
                    open ? 'top-[4px] rotate-45' : 'top-0'
                  }`}
                />
                <span
                  className={`absolute left-0 right-0 h-px bg-[color:var(--stone)] transition-transform ${
                    open ? 'top-[4px] -rotate-45' : 'top-[8px]'
                  }`}
                />
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden overflow-hidden border-t border-[color:var(--rule-dark)] bg-[color:var(--ink)]"
          >
            <nav className="px-5 py-4 flex flex-col">
              {LINKS.map((l) => (
                <button
                  key={l.slug}
                  data-testid={SITE.navLink(`m-${l.slug}`)}
                  onClick={() => handleNav(l.target)}
                  className="text-left py-3 border-b border-[color:var(--rule-dark)] small-caps text-[12px] text-[color:var(--stone)] hover:text-[color:var(--gold-2)] transition-colors"
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={() => {
                  setOpen(false);
                  onBook?.();
                }}
                className="mt-5 btn-primary justify-center"
              >
                Start a Conversation
              </button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Nav;
